/**
 * Encodes string into url safe string, using base64, then replacing "+" with "-", "/" with "_"
 * and removes padding "=" at the end of the base64 string.
 *
 * @param input - string
 * @returns url safe string
 */
export const urlSafeEncode = (input: string): string => {
  const base64 = window.btoa(input);
  const base64RemapChars: { [index: string]: string } = { '+': '-', '/': '_', '=': '' };
  return base64.replace(/[+/=]/g, (char: string) => base64RemapChars[char]);
};

/**
 * Uses SHA-256 mechanism to hash input string.
 *
 * @param input - input string to be hashed
 * @param returnHex - controls if the returned value is represented as a hex string.
 * @returns hashed string
 */
export const sha256hash = async (input: string, returnHex = false): Promise<string> => {
  const buffer = new TextEncoder().encode(input);
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashString = String.fromCharCode(...hashArray);
  const hashHexString = hashArray.map((byte) => byte.toString(16).padStart(2, '0')).join('');
  return returnHex ? hashHexString : hashString;
};

/**
 * Creates a random string of a desired length (default 64 chars).
 *
 * @param length - desired length of string
 * @returns random string
 */
export const createRandomString = (length = 64): string => {
  let random = '';
  const charset = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
  const randomValues = Array.from(window.crypto.getRandomValues(new Uint8Array(length)));
  for (const value of randomValues) {
    random += charset[value % charset.length];
  }
  return random;
};
